import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Input } from '@driftt/tide-core'
import DropZone from 'react-dropzone'
import AppPreviewTile from './AppPreviewTile'
import { uploadFile } from 'api/fileApi'
import { updateIconUrl, updateAppName, updateAppShortDescription } from 'api/developerApi'
import cx from 'classnames'
import debounce from 'lodash/debounce'
import './style.css'

const MAX_ICON_SIZE_IN_BYTES = 500 * 1024
const UPDATE_DELAY_IN_MS = 300

class AppQuickView extends Component {

  static propTypes = {
    appId: PropTypes.string.isRequired,
    isLoading: PropTypes.bool.isRequired,
    showErrorNotification: PropTypes.func.isRequired,
    app: PropTypes.shape({
      name: PropTypes.string,
      shortDescription: PropTypes.string,
      iconUrl: PropTypes.string
    })
  }

  constructor(props) {
    super(props)
    this.state = {
      name: props.app.name,
      shortDescription: props.app.shortDescription,
      iconUrl: props.app.iconUrl,
      isIconUpdating: false
    }
  }

  componentWillReceiveProps(props) {
    this.setState({
      name: props.app.name,
      shortDescription: props.app.shortDescription,
      iconUrl: props.app.iconUrl
    })
  }

  handleFileUpload = ([ file ]) => {
    if (!file || this.props.isLoading) return

    const onError = (error) => this.props.showErrorNotification('We couldn’t upload your app icon. Please try again.')

    const onFileUploaded = (iconUrl) => {
      const { appId } = this.props

      const onIconUpdated = (data) => {
        this.setState({
          isIconUpdating: false,
          iconUrl
        })
      }

      return updateIconUrl({ appId, iconUrl })
        .then(onIconUpdated)
    }

    const onStateSet = () => uploadFile(file)
      .then(onFileUploaded)
      .catch(onError)

    this.setState({ isIconUpdating: true }, onStateSet)
  }

  updateAppName = (name) => {
    const { appId } = this.props
    const onError = (error) => this.props.showErrorNotification('We couldn’t update the name. Please try again.')

    updateAppName({ name, appId })
      .catch(onError)
  }

  updateAppNameDebounced = debounce(this.updateAppName, UPDATE_DELAY_IN_MS)

  onNameChanged = (event) => {
    const name = event.target.value
    const onStateSet = () => this.updateAppNameDebounced(name)
    this.setState({ name }, onStateSet)
  }

  updateShortDescription = (shortDescription) => {
    const { appId } = this.props
    const onError = (error) => this.props.showErrorNotification('We couldn’t update the short description. Please try again.')

    updateAppShortDescription({ shortDescription, appId })
      .catch(onError)
  }

  updateShortDescriptionDebounced = debounce(this.updateShortDescription, UPDATE_DELAY_IN_MS)

  onShortDescriptionChanged = (event) => {
    const shortDescription = event.target.value
    const onStateSet = () => this.updateShortDescriptionDebounced(shortDescription)
    this.setState({ shortDescription }, onStateSet)
  }

  render() {
    const { isIconUpdating, name, shortDescription, iconUrl } = this.state
    const { isLoading } = this.props

    return (
      <div className="app-quick-view">
        <span className="app-quick-view-title">
          Quick View Card
        </span>
        <p className="app-quick-view-description">
          This is how your app will display on the Integrations page.
        </p>
        <div className="app-quick-view-panes">
          <div className="app-quick-view-pane">
            <Input
              label="App Name (max 30 characters)"
              value={name}
              maxLength={30}
              onChange={this.onNameChanged}
              className={cx('app-quick-view-name', {
                'drift-input-loading': isLoading,
                'drift-input-loaded': !isLoading
              })}
            />
            <Input
              label="Short Description"
              value={shortDescription}
              placeholder="Add a short description..."
              maxLength={60}
              onChange={this.onShortDescriptionChanged}
              className={cx('app-quick-view-short-description', {
                'drift-input-loading': isLoading,
                'drift-input-loaded': !isLoading
              })}
            />
            <div className="app-quick-view-icon">
              <span className="app-quick-view-icon-title">
                App Icon
              </span>
              <div className="app-quick-view-icon-wrapper">
                <p className="app-quick-view-icon-instructions">
                  Icons must be no larger than 75px x 75px
                  <br/>
                  Accepted formats: .png, .jpg or .svg
                </p>
                <DropZone
                  className="app-quick-view-icon-upload"
                  disablePreview={true}
                  maxSize={MAX_ICON_SIZE_IN_BYTES}
                  accept=".png, .jpg, .svg"
                  onDrop={this.handleFileUpload}>
                  Upload Icon
                </DropZone>
              </div>
            </div>
          </div>
          <div className="app-quick-view-preview">
            <AppPreviewTile
              name={name}
              description={shortDescription}
              iconUrl={iconUrl}
              isIconUpdating={isLoading || isIconUpdating} />
          </div>
        </div>
      </div>
    )
  }
}

export default AppQuickView
